/**
 * Line Detection Debug Widget
 *
 * Displays debug information for line detection including:
 * - Position map visualization with RGBA channel toggles and band simulation
 * - Pipeline control panel for adjusting processing steps
 */

import { DebugWidget, createWidgetContainer } from '../../../utils/DebugOverlay';
import { PipelineControlPanel, PipelineInterface } from '../../../ui/pipeline/PipelineControlPanel';
import { LineDetectionPipeline, LINE_DETECTION_DISTANCE_PARAMETERS } from '../modules/line-detection/LineDetectionPipeline';
import type { DepthSegmentationPipeline } from '../../../utils/segmentation/DepthSegmentationCore';

/**
 * Serializable version of LinePrompt for JSON export/import.
 * Uses plain objects instead of THREE.Vector3 for serialization.
 */
export interface SerializableLinePrompt {
  id: string;
  worldPoint: { x: number; y: number; z: number };
  direction: { x: number; y: number; z: number };
  positiveLength?: number;
  negativeLength?: number;
  isTemporary?: boolean;
}

export class LineDetectionDebugWidget implements DebugWidget {
  name = 'line-detection-debug';
  container: HTMLElement;
  private maxDisplayWidth = 376; // 400px container - 24px padding
  private statsDisplay: HTMLElement;
  private pipelinePanel: PipelineControlPanel | null = null;
  private onPipelineChangedCallback?: () => void;
  private onDepthPipelineChangedCallback?: () => void;
  private onPolygonPipelineChangedCallback?: () => void;
  private onSpawnTextureCallback?: (type: 'position-map' | 'density-map' | 'segmentation', imageData: ImageData) => void;
  private onClearTextureCallback?: (type: 'position-map' | 'density-map' | 'segmentation') => void;
  private pixelsToCm: ((pixels: number) => number) | null = null;
  private cmToPixels: ((cm: number) => number) | null = null;

  // Line prompt copy/paste callbacks
  private onGetLinePromptsCallback?: () => SerializableLinePrompt[];
  private onSetLinePromptsCallback?: (prompts: SerializableLinePrompt[]) => void;

  // Processed polygon copy callback
  private onGetProcessedPolygonCallback?: () => object | null;

  // Position map display
  private positionMapCanvas: HTMLCanvasElement;
  private positionMapCtx: CanvasRenderingContext2D;
  private lastPositionMap: ImageData | null = null;
  private positionMapChannels = { r: true, g: true, b: true, a: true };
  private positionMapMirrorB = false;
  /** Display value range filters (0-255); pixels outside render black. */
  private positionMapRange = { min: 0, max: 255 };
  private densityMapRange = { min: 0, max: 255 };

  // Density map display
  private densityMapCanvas: HTMLCanvasElement;
  private densityMapCtx: CanvasRenderingContext2D;
  private lastDensityMap: ImageData | null = null;

  // Require Enter to confirm mask (instead of auto-confirm on mouse up)
  private _requireEnterConfirm = false;

  // Allow box extrusion even when it breaks the shape (debug override)
  private _allowInvalidBoxShape = false;

  // Density snapper debug visualization
  private _densitySnapDebug = false;
  private onDensitySnapDebugChangedCallback?: (enabled: boolean) => void;

  // Band simulation
  private bandSimCanvas: HTMLCanvasElement;
  private bandSimCtx: CanvasRenderingContext2D;
  private bandSimEnabled = false;
  private bandSimCount = 4;

  // Segmentation result display
  private segCanvas: HTMLCanvasElement;
  private segCtx: CanvasRenderingContext2D;
  private segEnabled = false;
  private lastSegColoredData: Uint8ClampedArray | null = null;
  private lastSegWidth = 0;
  private lastSegHeight = 0;

  constructor() {
    this.container = createWidgetContainer('📐 Line Detection Debug');

    // Stats display
    this.statsDisplay = document.createElement('div');
    this.statsDisplay.style.cssText = `
      margin-bottom: 12px;
      padding: 8px;
      background: rgba(0, 0, 0, 0.3);
      border-radius: 4px;
      font-size: 11px;
      font-family: monospace;
      color: rgba(255, 255, 255, 0.9);
    `;
    this.statsDisplay.textContent = 'No detection data yet';
    this.container.appendChild(this.statsDisplay);

    // Require Enter to confirm checkbox
    const enterConfirmLabel = document.createElement('label');
    enterConfirmLabel.style.cssText = `
      display: flex;
      align-items: center;
      gap: 4px;
      cursor: pointer;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.9);
      user-select: none;
      margin-bottom: 8px;
    `;
    const enterConfirmCheckbox = document.createElement('input');
    enterConfirmCheckbox.type = 'checkbox';
    enterConfirmCheckbox.checked = this._requireEnterConfirm;
    enterConfirmCheckbox.style.cssText = `cursor: pointer; width: 13px; height: 13px;`;
    enterConfirmCheckbox.addEventListener('change', () => {
      this._requireEnterConfirm = enterConfirmCheckbox.checked;
    });
    enterConfirmLabel.appendChild(enterConfirmCheckbox);
    enterConfirmLabel.appendChild(document.createTextNode('Require Enter to confirm mask'));
    this.container.appendChild(enterConfirmLabel);

    // Density snap debug checkbox
    const snapDebugLabel = document.createElement('label');
    snapDebugLabel.style.cssText = `
      display: flex;
      align-items: center;
      gap: 4px;
      cursor: pointer;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.9);
      user-select: none;
      margin-bottom: 8px;
    `;
    const snapDebugCheckbox = document.createElement('input');
    snapDebugCheckbox.type = 'checkbox';
    snapDebugCheckbox.checked = this._densitySnapDebug;
    snapDebugCheckbox.style.cssText = `cursor: pointer; width: 13px; height: 13px;`;
    snapDebugCheckbox.addEventListener('change', () => {
      this._densitySnapDebug = snapDebugCheckbox.checked;
      this.onDensitySnapDebugChangedCallback?.(this._densitySnapDebug);
    });
    snapDebugLabel.appendChild(snapDebugCheckbox);
    snapDebugLabel.appendChild(document.createTextNode('Show density snap probes'));
    this.container.appendChild(snapDebugLabel);

    // Allow invalid box shape checkbox
    const invalidBoxLabel = document.createElement('label');
    invalidBoxLabel.style.cssText = `
      display: flex;
      align-items: center;
      gap: 4px;
      cursor: pointer;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.9);
      user-select: none;
      margin-bottom: 8px;
    `;
    const invalidBoxCheckbox = document.createElement('input');
    invalidBoxCheckbox.type = 'checkbox';
    invalidBoxCheckbox.checked = this._allowInvalidBoxShape;
    invalidBoxCheckbox.style.cssText = `cursor: pointer; width: 13px; height: 13px;`;
    invalidBoxCheckbox.addEventListener('change', () => {
      this._allowInvalidBoxShape = invalidBoxCheckbox.checked;
    });
    invalidBoxLabel.appendChild(invalidBoxCheckbox);
    invalidBoxLabel.appendChild(document.createTextNode('Allow box to break shape'));
    this.container.appendChild(invalidBoxLabel);

    // Position Map label
    const posMapLabel = document.createElement('div');
    posMapLabel.style.cssText = `
      margin-top: 12px;
      margin-bottom: 4px;
      font-size: 12px;
      font-weight: bold;
      color: rgba(255, 255, 255, 0.9);
    `;
    posMapLabel.textContent = 'Position Map:';
    this.container.appendChild(posMapLabel);

    // RGBA channel toggle checkboxes
    const channelToggles = document.createElement('div');
    channelToggles.style.cssText = `
      display: flex;
      gap: 12px;
      margin-bottom: 6px;
    `;
    const channels: { key: keyof LineDetectionDebugWidget['positionMapChannels']; label: string; color: string }[] = [
      { key: 'r', label: 'R', color: '#e74c3c' },
      { key: 'g', label: 'G', color: '#2ecc71' },
      { key: 'b', label: 'B', color: '#3498db' },
      { key: 'a', label: 'A', color: 'rgba(255,255,255,0.7)' },
    ];
    for (const ch of channels) {
      const label = document.createElement('label');
      label.style.cssText = `
        display: flex;
        align-items: center;
        gap: 4px;
        cursor: pointer;
        font-size: 12px;
        color: ${ch.color};
        user-select: none;
      `;
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.checked = true;
      checkbox.style.cssText = `cursor: pointer; width: 13px; height: 13px;`;
      checkbox.addEventListener('change', () => {
        this.positionMapChannels[ch.key] = checkbox.checked;
        this.renderPositionMap();
      });
      label.appendChild(checkbox);
      label.appendChild(document.createTextNode(ch.label));
      channelToggles.appendChild(label);
    }
    this.container.appendChild(channelToggles);

    // Mirror B checkbox
    const mirrorBLabel = document.createElement('label');
    mirrorBLabel.style.cssText = `
      display: flex;
      align-items: center;
      gap: 4px;
      cursor: pointer;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.7);
      user-select: none;
      margin-bottom: 6px;
    `;
    const mirrorBCheckbox = document.createElement('input');
    mirrorBCheckbox.type = 'checkbox';
    mirrorBCheckbox.checked = false;
    mirrorBCheckbox.style.cssText = `cursor: pointer; width: 13px; height: 13px;`;
    mirrorBCheckbox.addEventListener('change', () => {
      this.positionMapMirrorB = mirrorBCheckbox.checked;
      this.renderPositionMap();
    });
    mirrorBLabel.appendChild(mirrorBCheckbox);
    mirrorBLabel.appendChild(document.createTextNode('Mirror B (255 - b for b < 128)'));
    this.container.appendChild(mirrorBLabel);

    // Value-range filter (dual-knob) for the position map
    this.container.appendChild(
      this.createRangeSlider((min, max) => {
        this.positionMapRange.min = min;
        this.positionMapRange.max = max;
        this.renderPositionMap();
      }),
    );

    // Position map canvas
    this.positionMapCanvas = document.createElement('canvas');
    this.positionMapCanvas.style.cssText = `
      width: 100%;
      max-width: 100%;
      height: auto;
      border: 2px solid #2ecc71;
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.5);
      display: block;
      object-fit: contain;
    `;
    const posCtx = this.positionMapCanvas.getContext('2d');
    if (!posCtx) throw new Error('Failed to get 2d context for position map canvas');
    this.positionMapCtx = posCtx;
    this.container.appendChild(this.positionMapCanvas);

    // Spawn button row for position map
    const spawnPosMapRow = document.createElement('div');
    spawnPosMapRow.style.cssText = `
      margin-top: 6px;
      margin-bottom: 8px;
      display: flex;
      gap: 8px;
    `;
    const spawnPosMapBtn = document.createElement('button');
    spawnPosMapBtn.textContent = '🌍 Spawn';
    spawnPosMapBtn.style.cssText = `
      flex: 1;
      background: #2c3e50;
      border: 1px solid #3498db;
      color: #fff;
      padding: 6px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 11px;
    `;
    spawnPosMapBtn.addEventListener('click', () => {
      // Spawn the currently rendered canvas content (with channel toggles/mirror applied)
      if (this.positionMapCanvas.width > 0 && this.positionMapCanvas.height > 0 && this.onSpawnTextureCallback) {
        const imageData = this.positionMapCtx.getImageData(0, 0, this.positionMapCanvas.width, this.positionMapCanvas.height);
        this.onSpawnTextureCallback('position-map', imageData);
      }
    });
    spawnPosMapRow.appendChild(spawnPosMapBtn);

    const clearPosMapBtn = document.createElement('button');
    clearPosMapBtn.textContent = '🗑️ Clear';
    clearPosMapBtn.style.cssText = `
      flex: 1;
      background: #2c3e50;
      border: 1px solid #e74c3c;
      color: #fff;
      padding: 6px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 11px;
    `;
    clearPosMapBtn.addEventListener('click', () => {
      if (this.onClearTextureCallback) {
        this.onClearTextureCallback('position-map');
      }
    });
    spawnPosMapRow.appendChild(clearPosMapBtn);

    this.container.appendChild(spawnPosMapRow);

    // Show placeholder initially
    this.renderPositionMap();

    // Density Map section
    const densityMapLabel = document.createElement('div');
    densityMapLabel.style.cssText = `
      margin-top: 12px;
      margin-bottom: 4px;
      font-size: 12px;
      font-weight: bold;
      color: rgba(255, 255, 255, 0.9);
    `;
    densityMapLabel.textContent = 'Density Map:';
    this.container.appendChild(densityMapLabel);

    // Value-range filter (dual-knob) for the density map
    this.container.appendChild(
      this.createRangeSlider((min, max) => {
        this.densityMapRange.min = min;
        this.densityMapRange.max = max;
        this.renderDensityMap();
      }),
    );

    // Density map canvas
    this.densityMapCanvas = document.createElement('canvas');
    this.densityMapCanvas.style.cssText = `
      width: 100%;
      max-width: 100%;
      height: auto;
      border: 2px solid #f39c12;
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.5);
      display: block;
      object-fit: contain;
    `;
    const densityCtx = this.densityMapCanvas.getContext('2d');
    if (!densityCtx) throw new Error('Failed to get 2d context for density map canvas');
    this.densityMapCtx = densityCtx;
    this.container.appendChild(this.densityMapCanvas);

    // Spawn button row for density map
    const spawnDensityMapRow = document.createElement('div');
    spawnDensityMapRow.style.cssText = `
      margin-top: 6px;
      margin-bottom: 8px;
      display: flex;
      gap: 8px;
    `;
    const spawnDensityMapBtn = document.createElement('button');
    spawnDensityMapBtn.textContent = '🌍 Spawn';
    spawnDensityMapBtn.style.cssText = `
      flex: 1;
      background: #2c3e50;
      border: 1px solid #f39c12;
      color: #fff;
      padding: 6px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 11px;
    `;
    spawnDensityMapBtn.addEventListener('click', () => {
      // Spawn the currently rendered canvas content
      if (this.densityMapCanvas.width > 0 && this.densityMapCanvas.height > 0 && this.onSpawnTextureCallback) {
        const imageData = this.densityMapCtx.getImageData(0, 0, this.densityMapCanvas.width, this.densityMapCanvas.height);
        this.onSpawnTextureCallback('density-map', imageData);
      }
    });
    spawnDensityMapRow.appendChild(spawnDensityMapBtn);

    const clearDensityMapBtn = document.createElement('button');
    clearDensityMapBtn.textContent = '🗑️ Clear';
    clearDensityMapBtn.style.cssText = `
      flex: 1;
      background: #2c3e50;
      border: 1px solid #e74c3c;
      color: #fff;
      padding: 6px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 11px;
    `;
    clearDensityMapBtn.addEventListener('click', () => {
      if (this.onClearTextureCallback) {
        this.onClearTextureCallback('density-map');
      }
    });
    spawnDensityMapRow.appendChild(clearDensityMapBtn);

    this.container.appendChild(spawnDensityMapRow);

    // Show placeholder initially
    this.renderDensityMap();

    // Band simulation section
    const bandSimHeader = document.createElement('div');
    bandSimHeader.style.cssText = `
      margin-top: 12px;
      margin-bottom: 4px;
      display: flex;
      align-items: center;
      gap: 8px;
    `;

    const bandSimToggle = document.createElement('label');
    bandSimToggle.style.cssText = `
      display: flex;
      align-items: center;
      gap: 4px;
      cursor: pointer;
      font-size: 12px;
      font-weight: bold;
      color: rgba(255, 255, 255, 0.9);
      user-select: none;
    `;
    const bandSimCheckbox = document.createElement('input');
    bandSimCheckbox.type = 'checkbox';
    bandSimCheckbox.checked = false;
    bandSimCheckbox.style.cssText = `cursor: pointer; width: 13px; height: 13px;`;

    const bandCountLabel = document.createElement('label');
    bandCountLabel.style.cssText = `
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.7);
      margin-left: 8px;
    `;
    const bandCountInput = document.createElement('input');
    bandCountInput.type = 'number';
    bandCountInput.min = '1';
    bandCountInput.max = '64';
    bandCountInput.value = String(this.bandSimCount);
    bandCountInput.style.cssText = `
      width: 48px;
      background: rgba(0,0,0,0.4);
      border: 1px solid rgba(255,255,255,0.3);
      border-radius: 3px;
      color: #fff;
      font-size: 12px;
      padding: 2px 4px;
    `;

    const redraw = () => {
      if (this.bandSimEnabled) {
        this.bandSimCanvas.style.display = 'block';
        this.showBandSimulation();
      } else {
        this.bandSimCanvas.style.display = 'none';
      }
    };

    bandSimCheckbox.addEventListener('change', () => {
      this.bandSimEnabled = bandSimCheckbox.checked;
      redraw();
    });
    bandCountInput.addEventListener('change', () => {
      this.bandSimCount = Math.max(1, Math.min(64, parseInt(bandCountInput.value, 10) || 4));
      bandCountInput.value = String(this.bandSimCount);
      redraw();
    });

    bandSimToggle.appendChild(bandSimCheckbox);
    bandSimToggle.appendChild(document.createTextNode('Band Simulation'));
    bandCountLabel.appendChild(document.createTextNode('Bands:'));
    bandCountLabel.appendChild(bandCountInput);
    bandSimHeader.appendChild(bandSimToggle);
    bandSimHeader.appendChild(bandCountLabel);
    this.container.appendChild(bandSimHeader);

    this.bandSimCanvas = document.createElement('canvas');
    this.bandSimCanvas.style.cssText = `
      width: 100%;
      max-width: 100%;
      height: auto;
      border: 2px solid #f39c12;
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.5);
      display: none;
      object-fit: contain;
    `;
    const bandSimCtx = this.bandSimCanvas.getContext('2d');
    if (!bandSimCtx) throw new Error('Failed to get 2d context for band simulation canvas');
    this.bandSimCtx = bandSimCtx;
    this.container.appendChild(this.bandSimCanvas);

    // Segmentation result section
    const segHeader = document.createElement('div');
    segHeader.style.cssText = `
      margin-top: 12px;
      margin-bottom: 4px;
      display: flex;
      align-items: center;
      gap: 8px;
    `;

    const segToggle = document.createElement('label');
    segToggle.style.cssText = `
      display: flex;
      align-items: center;
      gap: 4px;
      cursor: pointer;
      font-size: 12px;
      font-weight: bold;
      color: rgba(255, 255, 255, 0.9);
      user-select: none;
    `;
    const segCheckbox = document.createElement('input');
    segCheckbox.type = 'checkbox';
    segCheckbox.checked = false;
    segCheckbox.style.cssText = `cursor: pointer; width: 13px; height: 13px;`;
    segCheckbox.addEventListener('change', () => {
      this.segEnabled = segCheckbox.checked;
      if (this.segEnabled) {
        this.segCanvas.style.display = 'block';
        this.renderSegmentation();
      } else {
        this.segCanvas.style.display = 'none';
      }
    });
    segToggle.appendChild(segCheckbox);
    segToggle.appendChild(document.createTextNode('Segmentation Result'));
    segHeader.appendChild(segToggle);
    this.container.appendChild(segHeader);

    this.segCanvas = document.createElement('canvas');
    this.segCanvas.style.cssText = `
      width: 100%;
      max-width: 100%;
      height: auto;
      border: 2px solid #e74c3c;
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.5);
      display: none;
      object-fit: contain;
    `;
    const segCtx = this.segCanvas.getContext('2d');
    if (!segCtx) throw new Error('Failed to get 2d context for segmentation canvas');
    this.segCtx = segCtx;
    this.container.appendChild(this.segCanvas);

    // Spawn button row for segmentation
    const spawnSegRow = document.createElement('div');
    spawnSegRow.style.cssText = `
      margin-top: 6px;
      margin-bottom: 8px;
      display: none;
      gap: 8px;
    `;
    spawnSegRow.id = 'spawn-seg-row';
    const spawnSegBtn = document.createElement('button');
    spawnSegBtn.textContent = '🌍 Spawn';
    spawnSegBtn.style.cssText = `
      flex: 1;
      background: #2c3e50;
      border: 1px solid #e74c3c;
      color: #fff;
      padding: 6px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 11px;
    `;
    spawnSegBtn.addEventListener('click', () => {
      // Spawn the currently rendered canvas content
      if (this.segCanvas.width > 0 && this.segCanvas.height > 0 && this.onSpawnTextureCallback) {
        const imageData = this.segCtx.getImageData(0, 0, this.segCanvas.width, this.segCanvas.height);
        this.onSpawnTextureCallback('segmentation', imageData);
      }
    });
    spawnSegRow.appendChild(spawnSegBtn);

    const clearSegBtn = document.createElement('button');
    clearSegBtn.textContent = '🗑️ Clear';
    clearSegBtn.style.cssText = `
      flex: 1;
      background: #2c3e50;
      border: 1px solid #c0392b;
      color: #fff;
      padding: 6px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 11px;
    `;
    clearSegBtn.addEventListener('click', () => {
      if (this.onClearTextureCallback) {
        this.onClearTextureCallback('segmentation');
      }
    });
    spawnSegRow.appendChild(clearSegBtn);

    this.container.appendChild(spawnSegRow);

    // Show/hide spawn button when segmentation toggle changes
    segCheckbox.addEventListener('change', () => {
      spawnSegRow.style.display = segCheckbox.checked ? 'flex' : 'none';
    });

    // Test Fixture Export section
    const fixtureLabel = document.createElement('div');
    fixtureLabel.style.cssText = `
      margin-top: 12px;
      margin-bottom: 4px;
      font-size: 12px;
      font-weight: bold;
      color: rgba(255, 255, 255, 0.9);
    `;
    fixtureLabel.textContent = 'Test Fixture Export:';
    this.container.appendChild(fixtureLabel);

    // Description input
    const descriptionRow = document.createElement('div');
    descriptionRow.style.cssText = `
      margin-bottom: 6px;
    `;
    const descriptionInput = document.createElement('input');
    descriptionInput.type = 'text';
    descriptionInput.placeholder = 'Description (e.g., "L-shaped room")';
    descriptionInput.style.cssText = `
      width: 100%;
      box-sizing: border-box;
      background: rgba(0,0,0,0.4);
      border: 1px solid rgba(255,255,255,0.3);
      border-radius: 4px;
      color: #fff;
      font-size: 12px;
      padding: 6px 8px;
    `;
    descriptionRow.appendChild(descriptionInput);
    this.container.appendChild(descriptionRow);

    // Expected valid checkbox
    const expectedValidLabel = document.createElement('label');
    expectedValidLabel.style.cssText = `
      display: flex;
      align-items: center;
      gap: 6px;
      cursor: pointer;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.9);
      user-select: none;
      margin-bottom: 8px;
    `;
    const expectedValidCheckbox = document.createElement('input');
    expectedValidCheckbox.type = 'checkbox';
    expectedValidCheckbox.checked = true;
    expectedValidCheckbox.style.cssText = `cursor: pointer; width: 13px; height: 13px;`;
    expectedValidLabel.appendChild(expectedValidCheckbox);
    expectedValidLabel.appendChild(document.createTextNode('Expected Valid (should produce non-self-intersecting shape)'));
    this.container.appendChild(expectedValidLabel);

    // Export fixture button
    const exportFixtureBtn = document.createElement('button');
    exportFixtureBtn.textContent = '📦 Export Test Fixture';
    exportFixtureBtn.style.cssText = `
      width: 100%;
      background: #27ae60;
      border: 1px solid #2ecc71;
      color: #fff;
      padding: 8px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 12px;
      font-weight: bold;
      margin-bottom: 8px;
    `;
    exportFixtureBtn.addEventListener('click', () => {
      if (this.onGetLinePromptsCallback) {
        const prompts = this.onGetLinePromptsCallback();
        const description = descriptionInput.value || 'unnamed_fixture';
        const fixture = {
          description: description,
          prompts: prompts.map((p) => ({
            id: p.id,
            worldPoint: p.worldPoint,
            direction: p.direction,
            screenPosition: { x: 0, y: 0 },
          })),
          expectedValid: expectedValidCheckbox.checked,
        };
        const json = JSON.stringify(fixture, null, 2);

        // Create filename from description (replace spaces with underscores, remove special chars)
        const filename =
          description
            .toLowerCase()
            .replace(/\s+/g, '_')
            .replace(/[^a-z0-9_-]/g, '') + '.json';

        // Download as file
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        exportFixtureBtn.textContent = '✓ Downloaded!';
        setTimeout(() => {
          exportFixtureBtn.textContent = '📦 Export Test Fixture';
        }, 2000);
      }
    });
    this.container.appendChild(exportFixtureBtn);

    // Line Prompts section - copy/paste for test creation
    const linePromptsLabel = document.createElement('div');
    linePromptsLabel.style.cssText = `
      margin-top: 12px;
      margin-bottom: 4px;
      font-size: 12px;
      font-weight: bold;
      color: rgba(255, 255, 255, 0.9);
    `;
    linePromptsLabel.textContent = 'Line Prompts (raw):';
    this.container.appendChild(linePromptsLabel);

    // Copy/Paste button row
    const linePromptsRow = document.createElement('div');
    linePromptsRow.style.cssText = `
      margin-top: 6px;
      margin-bottom: 8px;
      display: flex;
      gap: 8px;
    `;

    const copyPromptsBtn = document.createElement('button');
    copyPromptsBtn.textContent = '📋 Copy JSON';
    copyPromptsBtn.style.cssText = `
      flex: 1;
      background: #2c3e50;
      border: 1px solid #9b59b6;
      color: #fff;
      padding: 6px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 11px;
    `;
    copyPromptsBtn.addEventListener('click', () => {
      if (this.onGetLinePromptsCallback) {
        const prompts = this.onGetLinePromptsCallback();
        const json = JSON.stringify(prompts, null, 2);
        navigator.clipboard
          .writeText(json)
          .then(() => {
            copyPromptsBtn.textContent = '✓ Copied!';
            setTimeout(() => {
              copyPromptsBtn.textContent = '📋 Copy JSON';
            }, 1500);
          })
          .catch((err) => {
            console.error('[LineDetectionDebugWidget] Failed to copy to clipboard:', err);
          });
      }
    });
    linePromptsRow.appendChild(copyPromptsBtn);

    const pastePromptsBtn = document.createElement('button');
    pastePromptsBtn.textContent = '📥 Paste JSON';
    pastePromptsBtn.style.cssText = `
      flex: 1;
      background: #2c3e50;
      border: 1px solid #27ae60;
      color: #fff;
      padding: 6px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 11px;
    `;
    pastePromptsBtn.addEventListener('click', () => {
      navigator.clipboard
        .readText()
        .then((text) => {
          try {
            const prompts = JSON.parse(text) as SerializableLinePrompt[];
            if (Array.isArray(prompts) && this.onSetLinePromptsCallback) {
              this.onSetLinePromptsCallback(prompts);
              pastePromptsBtn.textContent = '✓ Pasted!';
              setTimeout(() => {
                pastePromptsBtn.textContent = '📥 Paste JSON';
              }, 1500);
            } else {
              throw new Error('Invalid format: expected array');
            }
          } catch (err) {
            console.error('[LineDetectionDebugWidget] Failed to parse clipboard JSON:', err);
            pastePromptsBtn.textContent = '✗ Error';
            setTimeout(() => {
              pastePromptsBtn.textContent = '📥 Paste JSON';
            }, 1500);
          }
        })
        .catch((err) => {
          console.error('[LineDetectionDebugWidget] Failed to read clipboard:', err);
        });
    });
    linePromptsRow.appendChild(pastePromptsBtn);

    this.container.appendChild(linePromptsRow);

    // Polygon copy button row
    const polygonRow = document.createElement('div');
    polygonRow.style.cssText = `
      margin-top: 6px;
      margin-bottom: 8px;
      display: flex;
      gap: 8px;
    `;

    const copyPolygonBtn = document.createElement('button');
    copyPolygonBtn.textContent = '📋 Copy Polygon';
    copyPolygonBtn.style.cssText = `
      flex: 1;
      background: #2c3e50;
      border: 1px solid #e67e22;
      color: #fff;
      padding: 6px 12px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 11px;
    `;
    copyPolygonBtn.addEventListener('click', () => {
      if (this.onGetProcessedPolygonCallback) {
        const polygon = this.onGetProcessedPolygonCallback();
        if (polygon) {
          const json = JSON.stringify(polygon, null, 2);
          navigator.clipboard
            .writeText(json)
            .then(() => {
              copyPolygonBtn.textContent = '✓ Copied!';
              setTimeout(() => {
                copyPolygonBtn.textContent = '📋 Copy Polygon';
              }, 1500);
            })
            .catch((err) => {
              console.error('[LineDetectionDebugWidget] Failed to copy polygon to clipboard:', err);
            });
        } else {
          copyPolygonBtn.textContent = '✗ No polygon';
          setTimeout(() => {
            copyPolygonBtn.textContent = '📋 Copy Polygon';
          }, 1500);
        }
      }
    });
    polygonRow.appendChild(copyPolygonBtn);

    this.container.appendChild(polygonRow);

    // Pipeline control panel placeholder (will be initialized via setPipeline)
  }

  /**
   * Whether the user must press Enter to confirm a mask selection
   * (instead of auto-confirming on mouse up / click).
   */
  get requireEnterConfirm(): boolean {
    return this._requireEnterConfirm;
  }

  /**
   * Whether box extrusion should be allowed even when it breaks the shape.
   * Debug override to inspect invalid shapes.
   */
  get allowInvalidBoxShape(): boolean {
    return this._allowInvalidBoxShape;
  }

  /**
   * Set callback for when density snap debug visualization is toggled.
   */
  setDensitySnapDebugCallback(callback: (enabled: boolean) => void): void {
    this.onDensitySnapDebugChangedCallback = callback;
  }

  /**
   * Set the unit converter functions for displaying distances in cm
   */
  setUnitConverters(pixelsToCm: (pixels: number) => number, cmToPixels: (cm: number) => number): void {
    this.pixelsToCm = pixelsToCm;
    this.cmToPixels = cmToPixels;

    // Pass converters to pipeline panel if it exists
    if (this.pipelinePanel) {
      this.pipelinePanel.setUnitConverters(pixelsToCm, cmToPixels);
    }
  }

  /**
   * Set the pipeline and create control panel
   */
  setPipeline(pipeline: LineDetectionPipeline, onPipelineChanged?: () => void): void {
    this.onPipelineChangedCallback = onPipelineChanged;

    // Create pipeline control panel if it doesn't exist
    if (!this.pipelinePanel) {
      this.pipelinePanel = new PipelineControlPanel({
        onPipelineChanged: (tabId: string) => {
          if (tabId === 'depth-segmentation') {
            this.onDepthPipelineChangedCallback?.();
          } else if (tabId === 'polygon') {
            this.onPolygonPipelineChangedCallback?.();
          } else {
            this.onPipelineChangedCallback?.();
          }
        },
      });

      // Pass unit converters if they were set before the pipeline panel was created
      if (this.pixelsToCm && this.cmToPixels) {
        this.pipelinePanel.setUnitConverters(this.pixelsToCm, this.cmToPixels);
      }

      // Add pipeline tab
      this.pipelinePanel.addTab({
        id: 'line-detection',
        label: 'Line Detection',
        pipeline: pipeline,
        distanceParameters: LINE_DETECTION_DISTANCE_PARAMETERS,
      });

      // Add pipeline panel to container
      this.container.appendChild(this.pipelinePanel.getElement());
    }
  }

  /**
   * Add a depth segmentation pipeline tab to the pipeline control panel.
   * Must be called after setPipeline so the panel exists.
   */
  setDepthPipeline(pipeline: DepthSegmentationPipeline, onPipelineChanged?: () => void): void {
    if (!this.pipelinePanel) return;

    this.onDepthPipelineChangedCallback = onPipelineChanged;

    this.pipelinePanel.addTab({
      id: 'depth-segmentation',
      label: 'Depth Segmentation',
      pipeline: pipeline as unknown as PipelineInterface,
    });
  }

  /**
   * Add a polygon processing pipeline tab to the pipeline control panel.
   * Must be called after setPipeline so the panel exists.
   */
  setPolygonPipeline(pipeline: PipelineInterface, onPipelineChanged?: () => void): void {
    if (!this.pipelinePanel) return;

    this.onPolygonPipelineChangedCallback = onPipelineChanged;

    this.pipelinePanel.addTab({
      id: 'polygon',
      label: 'Polygon',
      pipeline: pipeline,
    });
  }

  /**
   * Set callback for spawning textures in the 3D world.
   * Called when user clicks "Spawn" buttons.
   */
  setSpawnTextureCallback(callback: (type: 'position-map' | 'density-map' | 'segmentation', imageData: ImageData) => void): void {
    this.onSpawnTextureCallback = callback;
  }

  /**
   * Set callback for clearing spawned textures from the 3D world.
   * Called when user clicks "Clear" buttons.
   */
  setClearTextureCallback(callback: (type: 'position-map' | 'density-map' | 'segmentation') => void): void {
    this.onClearTextureCallback = callback;
  }

  /**
   * Set callback for getting current line prompts (for copy to clipboard).
   */
  setGetLinePromptsCallback(callback: () => SerializableLinePrompt[]): void {
    this.onGetLinePromptsCallback = callback;
  }

  /**
   * Set callback for setting line prompts (for paste from clipboard).
   */
  setSetLinePromptsCallback(callback: (prompts: SerializableLinePrompt[]) => void): void {
    this.onSetLinePromptsCallback = callback;
  }

  /**
   * Set callback for getting the processed polygon (for copy to clipboard).
   */
  setGetProcessedPolygonCallback(callback: () => object | null): void {
    this.onGetProcessedPolygonCallback = callback;
  }

  /**
   * Refresh the pipeline panel UI (e.g., after coordinate transformer is updated)
   */
  refreshPipelinePanel(): void {
    if (this.pipelinePanel) {
      this.pipelinePanel.refreshPassesList();
    }
  }

  /**
   * Set the position map image data to display
   */
  setPositionMap(imageData: ImageData | null): void {
    this.lastPositionMap = imageData;
    this.renderPositionMap();
  }

  /**
   * Set the density map image data to display
   */
  setDensityMap(imageData: ImageData | null): void {
    this.lastDensityMap = imageData;
    this.renderDensityMap();
  }

  /**
   * Render the density map
   */
  /**
   * Build a single-track, two-knob range slider (0-255) with a live value
   * label. The two overlapping native range inputs are click-through except
   * for their thumbs (styled via an injected stylesheet, since thumb
   * pseudo-elements can't be styled inline).
   */
  private createRangeSlider(onChange: (min: number, max: number) => void): HTMLElement {
    if (!document.getElementById('ldw-range-style')) {
      const style = document.createElement('style');
      style.id = 'ldw-range-style';
      style.textContent = `
        .ldw-range { position: relative; height: 18px; margin: 2px 0 6px; }
        .ldw-range input[type=range] { position: absolute; left: 0; top: 0; width: 100%; height: 18px; margin: 0; background: transparent; -webkit-appearance: none; appearance: none; pointer-events: none; }
        .ldw-range input[type=range]::-webkit-slider-runnable-track { height: 4px; margin-top: 7px; background: transparent; border-radius: 2px; }
        .ldw-range input[type=range].ldw-track::-webkit-slider-runnable-track { background: rgba(255, 255, 255, 0.25); }
        .ldw-range input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; pointer-events: auto; width: 12px; height: 12px; margin-top: -4px; border-radius: 50%; background: #3498db; cursor: pointer; }
        .ldw-range input[type=range]::-moz-range-track { height: 4px; background: transparent; }
        .ldw-range input[type=range].ldw-track::-moz-range-track { background: rgba(255, 255, 255, 0.25); }
        .ldw-range input[type=range]::-moz-range-thumb { pointer-events: auto; width: 12px; height: 12px; border: none; border-radius: 50%; background: #3498db; cursor: pointer; }
      `;
      document.head.appendChild(style);
    }

    const wrapper = document.createElement('div');
    wrapper.style.cssText = `margin-bottom: 6px;`;

    // The captured maps top out around 222-223 rather than 255, so the
    // knobs run to 223 to give the whole slider travel useful resolution.
    // The max knob at its top end means "no upper bound" (255), so nothing
    // is ever clipped by the slider ceiling itself.
    const KNOB_MAX = 223;

    const header = document.createElement('div');
    header.style.cssText = `font-size: 11px; color: rgba(255, 255, 255, 0.7); display: flex; justify-content: space-between;`;
    const title = document.createElement('span');
    title.textContent = 'Range';
    const value = document.createElement('span');
    value.textContent = '0 – 255';
    header.appendChild(title);
    header.appendChild(value);
    wrapper.appendChild(header);

    const track = document.createElement('div');
    track.className = 'ldw-range';

    const makeKnob = (initial: number, drawsTrack: boolean): HTMLInputElement => {
      const input = document.createElement('input');
      input.type = 'range';
      input.min = '0';
      input.max = String(KNOB_MAX);
      input.step = '1';
      input.value = String(initial);
      if (drawsTrack) input.className = 'ldw-track';
      return input;
    };
    const minKnob = makeKnob(0, true);
    const maxKnob = makeKnob(KNOB_MAX, false);

    const emit = (): void => {
      let min = Number(minKnob.value);
      let max = Number(maxKnob.value);
      if (min > max) [min, max] = [max, min];
      if (max >= KNOB_MAX) max = 255;
      value.textContent = `${min} – ${max}`;
      onChange(min, max);
    };
    minKnob.addEventListener('input', emit);
    maxKnob.addEventListener('input', emit);

    track.appendChild(minKnob);
    track.appendChild(maxKnob);
    wrapper.appendChild(track);
    return wrapper;
  }

  private renderDensityMap(): void {
    const imageData = this.lastDensityMap;
    if (!imageData) {
      this.densityMapCanvas.width = this.maxDisplayWidth;
      this.densityMapCanvas.height = 100;
      this.densityMapCtx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      this.densityMapCtx.fillRect(0, 0, this.densityMapCanvas.width, this.densityMapCanvas.height);
      this.densityMapCtx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      this.densityMapCtx.font = '12px monospace';
      this.densityMapCtx.textAlign = 'center';
      this.densityMapCtx.textBaseline = 'middle';
      this.densityMapCtx.fillText('No density map', this.densityMapCanvas.width / 2, this.densityMapCanvas.height / 2);
      return;
    }

    this.densityMapCanvas.width = imageData.width;
    this.densityMapCanvas.height = imageData.height;

    const aspectRatio = imageData.width / imageData.height;
    let displayWidth: number;
    let displayHeight: number;
    if (aspectRatio > 1) {
      displayWidth = Math.min(this.maxDisplayWidth, imageData.width);
      displayHeight = displayWidth / aspectRatio;
    } else {
      displayHeight = Math.min(this.maxDisplayWidth, imageData.height);
      displayWidth = displayHeight * aspectRatio;
    }

    this.densityMapCanvas.style.width = `${displayWidth}px`;
    this.densityMapCanvas.style.height = `${displayHeight}px`;

    const { min, max } = this.densityMapRange;
    if (min <= 0 && max >= 255) {
      this.densityMapCtx.putImageData(imageData, 0, 0);
    } else {
      // Black out pixels whose luminance falls outside the filter range.
      const filtered = new ImageData(new Uint8ClampedArray(imageData.data), imageData.width, imageData.height);
      const d = filtered.data;
      for (let i = 0; i < d.length; i += 4) {
        const lum = (d[i] + d[i + 1] + d[i + 2]) / 3;
        if (lum < min || lum > max) {
          d[i] = 0;
          d[i + 1] = 0;
          d[i + 2] = 0;
          d[i + 3] = 255;
        }
      }
      this.densityMapCtx.putImageData(filtered, 0, 0);
    }
  }

  /**
   * Render the position map with current channel/mirror settings
   */
  private renderPositionMap(): void {
    if (this.bandSimEnabled) this.showBandSimulation();

    const imageData = this.lastPositionMap;
    if (!imageData) {
      this.positionMapCanvas.width = this.maxDisplayWidth;
      this.positionMapCanvas.height = 100;
      this.positionMapCtx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      this.positionMapCtx.fillRect(0, 0, this.positionMapCanvas.width, this.positionMapCanvas.height);
      this.positionMapCtx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      this.positionMapCtx.font = '12px monospace';
      this.positionMapCtx.textAlign = 'center';
      this.positionMapCtx.textBaseline = 'middle';
      this.positionMapCtx.fillText('No position map', this.positionMapCanvas.width / 2, this.positionMapCanvas.height / 2);
      return;
    }

    this.positionMapCanvas.width = imageData.width;
    this.positionMapCanvas.height = imageData.height;

    const aspectRatio = imageData.width / imageData.height;
    let displayWidth: number;
    let displayHeight: number;
    if (aspectRatio > 1) {
      displayWidth = Math.min(this.maxDisplayWidth, imageData.width);
      displayHeight = displayWidth / aspectRatio;
    } else {
      displayHeight = Math.min(this.maxDisplayWidth, imageData.height);
      displayWidth = displayHeight * aspectRatio;
    }

    this.positionMapCanvas.style.width = `${displayWidth}px`;
    this.positionMapCanvas.style.height = `${displayHeight}px`;

    // Apply channel toggles, mirror-B and the value-range filter before drawing
    const { r, g, b, a } = this.positionMapChannels;
    const { min: rangeMin, max: rangeMax } = this.positionMapRange;
    const rangeActive = rangeMin > 0 || rangeMax < 255;
    const needsProcessing = !(r && g && b && a) || this.positionMapMirrorB || rangeActive;
    if (!needsProcessing) {
      this.positionMapCtx.putImageData(imageData, 0, 0);
    } else {
      const filtered = new ImageData(new Uint8ClampedArray(imageData.data), imageData.width, imageData.height);
      const d = filtered.data;
      const enabledColorChannels = [r, g, b].filter(Boolean).length;
      const soloChannel = enabledColorChannels === 1 && !a ? [r, g, b].findIndex(Boolean) : -1;
      for (let i = 0; i < d.length; i += 4) {
        if (this.positionMapMirrorB && d[i + 2] > 0 && d[i + 2] < 128) {
          d[i + 2] = 255 - d[i + 2];
        }
        // The value the range filter judges: whatever this pixel DISPLAYS —
        // the solo channel, the alpha view, or the max of enabled channels.
        let displayValue: number;
        if (soloChannel >= 0) {
          const val = d[i + soloChannel];
          d[i] = val;
          d[i + 1] = val;
          d[i + 2] = val;
          d[i + 3] = 255;
          displayValue = val;
        } else if (enabledColorChannels === 0 && a) {
          const val = d[i + 3];
          d[i] = val;
          d[i + 1] = val;
          d[i + 2] = val;
          d[i + 3] = 255;
          displayValue = val;
        } else {
          if (!r) d[i] = 0;
          if (!g) d[i + 1] = 0;
          if (!b) d[i + 2] = 0;
          d[i + 3] = a ? d[i + 3] : 255;
          displayValue = Math.max(r ? d[i] : 0, g ? d[i + 1] : 0, b ? d[i + 2] : 0);
        }
        if (rangeActive && (displayValue < rangeMin || displayValue > rangeMax)) {
          d[i] = 0;
          d[i + 1] = 0;
          d[i + 2] = 0;
          d[i + 3] = 255;
        }
      }
      this.positionMapCtx.putImageData(filtered, 0, 0);
    }
  }

  /**
   * Render a band-assignment simulation from the B channel of the position map
   */
  private showBandSimulation(): void {
    const imageData = this.lastPositionMap;
    if (!imageData) return;

    const { width, height } = imageData;
    this.bandSimCanvas.width = width;
    this.bandSimCanvas.height = height;

    const aspectRatio = width / height;
    let displayWidth: number;
    let displayHeight: number;
    if (aspectRatio > 1) {
      displayWidth = Math.min(this.maxDisplayWidth, width);
      displayHeight = displayWidth / aspectRatio;
    } else {
      displayHeight = Math.min(this.maxDisplayWidth, height);
      displayWidth = displayHeight * aspectRatio;
    }
    this.bandSimCanvas.style.width = `${displayWidth}px`;
    this.bandSimCanvas.style.height = `${displayHeight}px`;

    const bandCount = this.bandSimCount;
    const bandSize = 256 / bandCount;

    // Generate a distinct hue per band
    const bandColors: [number, number, number][] = [];
    for (let i = 0; i < bandCount; i++) {
      const hue = (i / bandCount) * 360;
      const h = hue / 60;
      const s = 0.8;
      const l = 0.5;
      const c = (1 - Math.abs(2 * l - 1)) * s;
      const x = c * (1 - Math.abs((h % 2) - 1));
      let cr = 0,
        cg = 0,
        cb = 0;
      if (h < 1) {
        cr = c;
        cg = x;
      } else if (h < 2) {
        cr = x;
        cg = c;
      } else if (h < 3) {
        cg = c;
        cb = x;
      } else if (h < 4) {
        cg = x;
        cb = c;
      } else if (h < 5) {
        cr = x;
        cb = c;
      } else {
        cr = c;
        cb = x;
      }
      const m = l - c / 2;
      bandColors.push([Math.round((cr + m) * 255), Math.round((cg + m) * 255), Math.round((cb + m) * 255)]);
    }

    const out = this.bandSimCtx.createImageData(width, height);
    const src = imageData.data;
    const od = out.data;
    for (let i = 0; i < width * height; i++) {
      const pi = i * 4;
      let v = src[pi + 2]; // B channel (depth/height) — sole segmentation signal
      if (this.positionMapMirrorB && v > 0 && v < 128) v = 255 - v;
      if (v === 0) {
        od[pi] = 0;
        od[pi + 1] = 0;
        od[pi + 2] = 0;
        od[pi + 3] = 255;
      } else {
        const band = Math.min(bandCount - 1, Math.floor(v / bandSize));
        const [br, bg, bb] = bandColors[band];
        od[pi] = br;
        od[pi + 1] = bg;
        od[pi + 2] = bb;
        od[pi + 3] = 255;
      }
    }
    this.bandSimCtx.putImageData(out, 0, 0);
  }

  /**
   * Format a pixel value as cm if converter is available
   */
  private formatDistance(pixels: number): string {
    if (this.pixelsToCm) {
      const cm = this.pixelsToCm(pixels);
      return `${cm.toFixed(1)}cm`;
    }
    return `${pixels}px`;
  }

  /**
   * Update statistics display
   */
  setStats(stats: {
    detectionTime: number;
    linesDetected: number;
    linesAfterFiltering: number;
    imageSize: { width: number; height: number };
    config: {
      minConfidence: number;
      minLineLength: number;
      houghThreshold: number;
    };
    stepTimings?: {
      capturePositionMap?: number;
      captureDensityMap?: number;
      segmentation?: number;
      lineDetection?: number;
      renderRegionGraph?: number;
      renderLines?: number;
      updateDebugWidget?: number;
      polygonProcessing?: number;
      total?: number;
    };
    mapResolutions?: {
      positionMap?: { width: number; height: number };
      densityMap?: { width: number; height: number };
    };
  }): void {
    const formatTime = (ms: number | undefined) => (ms !== undefined ? `${ms.toFixed(1)}ms` : '-');

    // Build map resolutions HTML if available
    let mapResolutionsHtml = '';
    if (stats.mapResolutions) {
      const m = stats.mapResolutions;
      mapResolutionsHtml = `
      <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.2);">
        <div style="margin-bottom: 4px;"><strong>Map Resolutions:</strong></div>
        <div style="margin-left: 8px; font-size: 10px;">
          ${m.positionMap ? `positionMap: ${m.positionMap.width}x${m.positionMap.height}<br>` : ''}
          ${m.densityMap ? `densityMap: ${m.densityMap.width}x${m.densityMap.height}` : ''}
        </div>
      </div>`;
    }

    // Build step timings HTML if available
    let stepTimingsHtml = '';
    if (stats.stepTimings) {
      const t = stats.stepTimings;
      const timingLines: string[] = [];
      if (t.capturePositionMap !== undefined) timingLines.push(`capturePositionMap: ${formatTime(t.capturePositionMap)}`);
      if (t.captureDensityMap !== undefined) timingLines.push(`captureDensityMap: ${formatTime(t.captureDensityMap)}`);
      if (t.segmentation !== undefined) timingLines.push(`segmentation: ${formatTime(t.segmentation)}`);
      if (t.lineDetection !== undefined) timingLines.push(`lineDetection: ${formatTime(t.lineDetection)}`);
      if (t.polygonProcessing !== undefined) timingLines.push(`polygonProcessing: ${formatTime(t.polygonProcessing)}`);
      if (t.renderRegionGraph !== undefined) timingLines.push(`renderRegionGraph: ${formatTime(t.renderRegionGraph)}`);
      if (t.renderLines !== undefined) timingLines.push(`renderLines: ${formatTime(t.renderLines)}`);
      if (t.updateDebugWidget !== undefined) timingLines.push(`updateDebugWidget: ${formatTime(t.updateDebugWidget)}`);
      if (t.total !== undefined) timingLines.push(`<strong>total: ${formatTime(t.total)}</strong>`);

      if (timingLines.length > 0) {
        stepTimingsHtml = `
        <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.2);">
          <div style="margin-bottom: 4px;"><strong>Step Timings:</strong></div>
          <div style="margin-left: 8px; font-size: 10px;">
            ${timingLines.join('<br>')}
          </div>
        </div>`;
      }
    }

    this.statsDisplay.innerHTML = `
      <div style="margin-bottom: 4px;"><strong>Detection Time:</strong> ${stats.detectionTime.toFixed(1)}ms</div>
      <div style="margin-bottom: 4px;"><strong>Image Size:</strong> ${stats.imageSize.width}x${stats.imageSize.height}</div>
      <div style="margin-bottom: 4px;"><strong>Lines Detected:</strong> ${stats.linesDetected}</div>
      <div style="margin-bottom: 4px;"><strong>After Filtering:</strong> ${stats.linesAfterFiltering}</div>
      <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.2);">
        <div style="margin-bottom: 2px;"><strong>Config:</strong></div>
        <div style="margin-left: 8px; font-size: 10px;">
          minConfidence: ${stats.config.minConfidence}<br>
          minLineLength: ${this.formatDistance(stats.config.minLineLength)}<br>
          houghThreshold: ${stats.config.houghThreshold}
        </div>
      </div>
      ${mapResolutionsHtml}
      ${stepTimingsHtml}
    `;
  }

  /**
   * Set the segmentation colored data to display
   */
  setSegmentationData(coloredData: Uint8ClampedArray | null, width: number, height: number): void {
    this.lastSegColoredData = coloredData;
    this.lastSegWidth = width;
    this.lastSegHeight = height;
    if (this.segEnabled) {
      this.renderSegmentation();
    }
  }

  /**
   * Render the segmentation result (coloredData encodes region IDs as RGB — remap to visible colors)
   */
  private renderSegmentation(): void {
    const coloredData = this.lastSegColoredData;
    if (!coloredData || this.lastSegWidth === 0) {
      this.segCanvas.width = this.maxDisplayWidth;
      this.segCanvas.height = 100;
      this.segCtx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      this.segCtx.fillRect(0, 0, this.segCanvas.width, this.segCanvas.height);
      this.segCtx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      this.segCtx.font = '12px monospace';
      this.segCtx.textAlign = 'center';
      this.segCtx.textBaseline = 'middle';
      this.segCtx.fillText('No segmentation data', this.segCanvas.width / 2, this.segCanvas.height / 2);
      return;
    }

    const w = this.lastSegWidth;
    const h = this.lastSegHeight;
    this.segCanvas.width = w;
    this.segCanvas.height = h;

    const aspectRatio = w / h;
    let displayWidth: number;
    let displayHeight: number;
    if (aspectRatio > 1) {
      displayWidth = Math.min(this.maxDisplayWidth, w);
      displayHeight = displayWidth / aspectRatio;
    } else {
      displayHeight = Math.min(this.maxDisplayWidth, h);
      displayWidth = displayHeight * aspectRatio;
    }
    this.segCanvas.style.width = `${displayWidth}px`;
    this.segCanvas.style.height = `${displayHeight}px`;

    // Remap region ID colors to visually distinct colors using a hash
    const regionColorMap = new Map<number, [number, number, number]>();
    const out = this.segCtx.createImageData(w, h);
    const od = out.data;

    for (let i = 0; i < w * h; i++) {
      const pi = i * 4;
      const r = coloredData[pi];
      const g = coloredData[pi + 1];
      const b = coloredData[pi + 2];
      const regId = r | (g << 8) | (b << 16);

      if (regId === 0) {
        od[pi] = 30;
        od[pi + 1] = 30;
        od[pi + 2] = 30;
        od[pi + 3] = 255;
        continue;
      }

      let color = regionColorMap.get(regId);
      if (!color) {
        // Generate a distinct hue from the region ID
        const hue = (regId * 137.508) % 360;
        const s = 0.7 + (regId % 3) * 0.1;
        const l = 0.45 + (regId % 5) * 0.05;
        const c = (1 - Math.abs(2 * l - 1)) * s;
        const hp = hue / 60;
        const x = c * (1 - Math.abs((hp % 2) - 1));
        let cr = 0,
          cg = 0,
          cb = 0;
        if (hp < 1) {
          cr = c;
          cg = x;
        } else if (hp < 2) {
          cr = x;
          cg = c;
        } else if (hp < 3) {
          cg = c;
          cb = x;
        } else if (hp < 4) {
          cg = x;
          cb = c;
        } else if (hp < 5) {
          cr = x;
          cb = c;
        } else {
          cr = c;
          cb = x;
        }
        const m = l - c / 2;
        color = [Math.round((cr + m) * 255), Math.round((cg + m) * 255), Math.round((cb + m) * 255)];
        regionColorMap.set(regId, color);
      }

      od[pi] = color[0];
      od[pi + 1] = color[1];
      od[pi + 2] = color[2];
      od[pi + 3] = 255;
    }

    this.segCtx.putImageData(out, 0, 0);
  }

  update(): void {
    // Widget updates via setter methods
  }

  destroy(): void {
    // Remove pipeline panel if it exists
    if (this.pipelinePanel) {
      const element = this.pipelinePanel.getElement();
      if (element.parentElement) {
        element.parentElement.removeChild(element);
      }
      this.pipelinePanel = null;
    }

    if (this.container.parentElement) {
      this.container.parentElement.removeChild(this.container);
    }
  }
}
